import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { api } from '../../src/services/api-new';

interface AdminAnalyticsProps {
    onBack?: () => void;
}

const AdminAnalytics: React.FC<AdminAnalyticsProps> = ({ onBack }) => {
    const [performanceData, setPerformanceData] = useState<any[]>([]);
    const [categoryDistribution, setCategoryDistribution] = useState<any[]>([]);
    const [isDataLoading, setIsDataLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setIsDataLoading(true);
            try {
                const [perfData, distData] = await Promise.all([
                    api.analytics.getPerformance(),
                    api.analytics.getDistribution()
                ]);
                setPerformanceData(Array.isArray(perfData) ? perfData : []);
                setCategoryDistribution(Array.isArray(distData) ? distData : []);
            } catch (error) {
                console.error("Failed to fetch analytics:", error);
            } finally {
                setIsDataLoading(false);
            }
        };
        fetchData();
    }, []);

    if (isDataLoading) {
        return (
            <div className="h-full flex items-center justify-center bg-slate-50 dark:bg-background-dark">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                    <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Processing Clinical Intelligence...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto bg-slate-50 dark:bg-background-dark p-6 lg:p-10 custom-scrollbar">
            <div className="max-w-7xl mx-auto space-y-10">

                {/* Header section */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="flex items-center gap-4">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800 hover:border-primary transition-all shadow-sm"
                            >
                                <span className="material-icons-round text-gray-600 dark:text-gray-300">arrow_back</span>
                            </button>
                        )}
                        <div>
                            <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em] mb-1">Clinic Intelligence</p>
                            <h1 className="text-4xl font-black text-primary-dark dark:text-white tracking-tight">Command Center</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800 p-1.5 rounded-2xl flex shadow-sm">
                            <button className="px-4 py-2 bg-slate-100 dark:bg-gray-800 rounded-xl text-[10px] font-black uppercase tracking-widest text-primary">Daily</button>
                            <button className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-gray-400">Weekly</button>
                            <button className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-gray-400">Monthly</button>
                        </div>
                        <button className="w-12 h-12 bg-primary text-white rounded-2xl flex items-center justify-center shadow-xl shadow-primary/30 hover:scale-110 active:scale-90 transition-all font-black">
                            <span className="material-icons-round">refresh</span>
                        </button>
                    </div>
                </div>

                {/* High Impact Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    <MetricCard title="Gross Revenue" value="$22,400" trend="+18.2%" trendUp={true} icon="payments" color="bg-green-600" />
                    <MetricCard title="Patient Intake" value="138" trend="+5.4%" trendUp={true} icon="pets" color="bg-blue-600" />
                    <MetricCard title="Surgery Load" value="21" trend="-2.1%" trendUp={false} icon="biotech" color="bg-purple-600" />
                    <MetricCard title="Clinic Health" value="98.4%" trend="Optimal" trendUp={true} icon="verified" color="bg-primary" />
                </div>

                {/* Main Analytical Content */}
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-10">

                    {/* Revenue Growth Chart */}
                    <div className="xl:col-span-2 bg-white dark:bg-surface-dark p-8 rounded-[2.5rem] shadow-xl shadow-black/5 border border-gray-100 dark:border-gray-800">
                        <div className="flex flex-col md:flex-row justify-between md:items-center gap-6 mb-10">
                            <div>
                                <h3 className="text-xl font-black text-primary-dark dark:text-white tracking-tight uppercase">Performance Velocity</h3>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Revenue vs Patient Consultations</p>
                            </div>
                            <div className="flex gap-6">
                                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-primary"></div><p className="text-[10px] font-black uppercase text-gray-400">Revenue</p></div>
                                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-primary/20"></div><p className="text-[10px] font-black uppercase text-gray-400">Volume</p></div>
                            </div>
                        </div>

                        <div style={{ width: '100%', height: 320, minHeight: 320 }} className="font-mono text-[10px]">
                            {performanceData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={320} debounce={100}>
                                    <AreaChart data={performanceData}>
                                        <defs>
                                            <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#327451" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#327451" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" opacity={0.05} />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontWeight: 'bold' }} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontWeight: 'bold' }} />
                                        <Tooltip
                                            contentStyle={{
                                                borderRadius: '24px',
                                                border: 'none',
                                                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
                                                backgroundColor: '#fff',
                                                padding: '16px'
                                            }}
                                            itemStyle={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase' }}
                                        />
                                        <Area type="monotone" dataKey="revenue" stroke="#327451" strokeWidth={4} fillOpacity={1} fill="url(#colorRev)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-400">No performance data</div>
                            )}
                        </div>
                    </div>

                    {/* Distribution Chart */}
                    <div className="bg-white dark:bg-surface-dark p-8 rounded-[2.5rem] shadow-xl shadow-black/5 border border-gray-100 dark:border-gray-800 flex flex-col h-[500px]">
                        <h3 className="text-xl font-black text-primary-dark dark:text-white mb-10 tracking-tight uppercase">Service Load</h3>

                        <div style={{ width: '100%', height: 400, minHeight: 400 }} className="relative">
                            {categoryDistribution.length > 0 ? (
                                <ResponsiveContainer width="100%" height={400} debounce={100}>
                                    <BarChart data={categoryDistribution} layout="vertical" margin={{ left: 0, right: 30 }}>
                                        <XAxis type="number" hide />
                                        <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontWeight: 'bold', fontSize: 10 }} width={80} />
                                        <Tooltip
                                            cursor={{ fill: 'transparent' }}
                                            contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                                        />
                                        <Bar dataKey="value" radius={[0, 10, 10, 0]} barSize={20}>
                                            {categoryDistribution.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-400">No data available</div>
                            )}
                        </div>

                        <div className="space-y-6 mt-8">
                            {categoryDistribution.map(item => (
                                <div key={item.name} className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></div>
                                        <span className="text-[11px] font-black text-gray-500 uppercase tracking-widest">{item.name}</span>
                                    </div>
                                    <span className="text-[11px] font-black text-primary-dark dark:text-white font-mono">{item.value}%</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Insights Panel */}
                    <div className="xl:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="bg-primary-dark/5 dark:bg-black/20 p-8 rounded-[2.5rem] border border-primary/10">
                            <div className="flex items-center gap-4 mb-6">
                                <span className="material-icons-round text-primary">tips_and_updates</span>
                                <h4 className="text-sm font-black text-primary-dark dark:text-primary uppercase tracking-widest">Growth Recommendation</h4>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium leading-relaxed">
                                Based on this week's data, <span className="font-black text-primary-dark dark:text-white">Preventative Care</span> volume is up by 15%. Consider allocating more slots for vaccinations in the coming week to maximize throughput.
                            </p>
                        </div>

                        <div className="bg-white dark:bg-surface-dark p-8 rounded-[2.5rem] border border-gray-100 dark:border-gray-800 shadow-sm flex items-center justify-between">
                            <div className="space-y-4">
                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Clinic Efficiency Score</h4>
                                <div className="flex items-end gap-3">
                                    <span className="text-5xl font-black text-primary-dark dark:text-white">92</span>
                                    <span className="text-sm font-black text-primary mb-1.5 uppercase tracking-widest">High</span>
                                </div>
                            </div>
                            <div className="w-24 h-24 rounded-full border-[10px] border-slate-100 dark:border-gray-800 flex items-center justify-center relative">
                                <div className="absolute inset-0 border-[10px] border-primary rounded-full border-t-transparent -rotate-45"></div>
                                <span className="material-icons-round text-3xl text-primary">bolt</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Sub-components ---
const MetricCard = ({ title, value, trend, trendUp, icon, color }: any) => (
    <div className="bg-white dark:bg-surface-dark p-8 rounded-[2.5rem] shadow-xl shadow-black/5 border border-gray-100 dark:border-gray-800 group hover:border-primary/20 transition-all">
        <div className="flex justify-between items-start mb-6">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-2xl ${color} shadow-black/10 group-hover:scale-110 transition-transform`}>
                <span className="material-icons-round text-2xl">{icon}</span>
            </div>
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest ${trendUp ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                <span className="material-icons-round text-xs font-black">{trendUp ? 'trending_up' : 'trending_down'}</span>
                {trend}
            </div>
        </div>
        <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{title}</p>
            <h3 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">{value}</h3>
        </div>
    </div>
);

export default AdminAnalytics;